import { useState, useContext } from "react";
import { img, isMobile } from "../utils/settings";
import { FaAnglesRight } from "react-icons/fa6";
import { AppContext } from "../State/Context";
import { ActionType } from "../State/State";
import { getWorldCoordsToCenterHex } from "../State/stateUtils";
import { Hexagen } from "../types";
import { askAPI } from "../api/api";
import { RequestMethod } from "../api/api";
import useAsyncEffect from "use-async-effect";
import { Icon } from "../components/Icon";

enum MenuLeftType {
  RECENTLY_ANIMATED = "recently_animated",
  ALL_TIME_TOP = "all_time_top",
  MONTH_TOP = "month_top",
  WEEK_TOP = "week_top",
  RECENTLY_CREATED = "recently_created",
  MOST_RARE_ALL_TIME = "most_rare_all_time",
}

export const MenuLeft = () => {
  const { state, dispatch } = useContext(AppContext);
  const { isMenuLeftOpen, statistic } = state;
  const [selectedType, setSelectedType] = useState<MenuLeftType>(
    MenuLeftType.RECENTLY_ANIMATED
  );

  const [hexagensList, setHexagensList] = useState<Hexagen[]>([]);

  const topPlayers = statistic?.richestPlayers || [];

  const tabs: { type: MenuLeftType; label: string }[] = [
    { type: MenuLeftType.RECENTLY_ANIMATED, label: "Recently Animated" },
    { type: MenuLeftType.RECENTLY_CREATED, label: "Recently Created" },
    { type: MenuLeftType.WEEK_TOP, label: "Week Top" },
    { type: MenuLeftType.MONTH_TOP, label: "Month Top" },
    { type: MenuLeftType.ALL_TIME_TOP, label: "AllTime Top" },
  ];

  const handlePlayerClick = (uid: string) => {
    dispatch({ type: ActionType.SELECT_PLAYER, uid });
    dispatch({ type: ActionType.TOGGLE_PLAYER_INFO_POPUP });
  };

  useAsyncEffect(async () => {
    if (isMenuLeftOpen) {
      const urlList = {
        [MenuLeftType.RECENTLY_ANIMATED]: "/api/hexagen/recent-videos",
        [MenuLeftType.RECENTLY_CREATED]: "/api/hexagen/recent",
        [MenuLeftType.ALL_TIME_TOP]: "/api/hexagen/most-upvoted-all-time",
        [MenuLeftType.MONTH_TOP]: "/api/hexagen/most-upvoted-last-month",
        [MenuLeftType.WEEK_TOP]: "/api/hexagen/most-upvoted-last-week",
        [MenuLeftType.MOST_RARE_ALL_TIME]: "/api/hexagen/most-rare-all-time",
      };

      const url = urlList[selectedType];
      const hexagens = await askAPI<Hexagen[]>(url, RequestMethod.GET);
      setHexagensList(hexagens || []);
    }
  }, [selectedType, isMenuLeftOpen]);

  return (
    <>
      {!isMobile() && topPlayers.length > 0 && (
        <div
          className="fixed user-select-none text-xs"
          style={{
            top: "50px",
            left: "34px",
          }}
        >
          <ul className="flex flex-col">
            {topPlayers.map((player, i) => (
              <li
                key={player.uid || i}
                className="bg-black text-amber-50 opacity-90 bg-opacity-70 px-2 pr-4 py-1 w-fit flex items-center"
              >
                {i === 0 && <span className="text-[16px] pr-2">👑 </span>}
                {i === 1 && <span className="text-[16px] pr-2">✨ </span>}
                {i === 2 && <span className="text-[16px] pr-2">💛 </span>}

                <span
                  className="cursor-pointer hover:underline"
                  onClick={() => player.uid && handlePlayerClick(player.uid)}
                >
                  {player.name}
                </span>
                <span className="text-[10px] pl-1">/{player.points}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className={`antique absolute left-0 top-[45px] bottom-[37px] transition-all duration-300 z-[1000000000] [transform:translateZ(0)] ${
          isMenuLeftOpen ? (isMobile() ? "w-full" : "w-[450px]") : "w-0"
        }`}
      >
        {!isMenuLeftOpen ? (
          <div
            className="absolute top-5 pt-[2px] pb-[1px] px-[3px] right-[-29px] antique cursor-pointer text-2xl hover:text-yellow-100 hover:bg-gray-800"
            onClick={() => dispatch({ type: ActionType.TOGGLE_MENU_LEFT })}
          >
            <Icon icon={FaAnglesRight} />
          </div>
        ) : (
          <div
            className="absolute right-[17px] top-[6px] select-none cursor-pointer text-[25px]"
            onClick={() => dispatch({ type: ActionType.TOGGLE_MENU_LEFT })}
          >
            ✕
          </div>
        )}

        <div className={`${isMenuLeftOpen ? "block" : "hidden"}`}>
          <div className="flex justify-between pt-8 pb-4 pr-2 overflow-hidden">
            <div className="flex justify-center max-w-[350px] mx-auto text-xs">
              {tabs.map((t, idx) => {
                const isActive = selectedType === t.type;
                const padding = idx === 0 || idx === tabs.length - 1 ? "pr-2" : "px-2";
                return (
                  <div
                    key={t.type}
                    className={`${padding} text-center select-none ${
                      isActive
                        ? "font-bold cursor-default"
                        : "underline hover:no-underline cursor-pointer"
                    }`}
                    onClick={() => setSelectedType(t.type)}
                  >
                    {t.label}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="px-4 h-[calc(100vh-165px)] overflow-y-auto">
            <div className="grid grid-cols-1 gap-4">
              {hexagensList.map((hexagen: Hexagen, i: number) => (
                <HexagenListItem
                  key={hexagen.uid || i}
                  hexagen={hexagen}
                  closeMenu={() =>
                    dispatch({ type: ActionType.TOGGLE_MENU_LEFT })
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface HexagenListItemProps {
  closeMenu: () => void;
  hexagen: Hexagen;
}

const HexagenListItem = ({ hexagen, closeMenu }: HexagenListItemProps) => {
  const { dispatch, state } = useContext(AppContext);

  const handleClick = () => {
    const x = hexagen.x;
    const y = hexagen.y;
    dispatch({ type: ActionType.SELECT_HEX, x, y });
    dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
    const coords = getWorldCoordsToCenterHex(x, y, state.worldScale);
    dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: coords.worldX, y: coords.worldY, logggable: true });
    closeMenu();
  };

  return (
    <div className="w-full h-auto cursor-pointer flex" onClick={handleClick}>
      <div className="w-[60%] h-auto flex items-center justify-center pl-4">
        <img
          src={img(`x${hexagen.x}y${hexagen.y}.png`)}
          alt={`top-hex-${hexagen.uid}`}
          className="max-w-full"
        />
      </div>
      <div className="w-[40%] text-xs p-2 pl-4 flex flex-col justify-center">
        <div className="mb-1">x: {hexagen.x}</div>
        <div className="mb-1">y: {hexagen.y}</div>
        <div className="break-words mb-1">prompt: {hexagen.prompt}</div>
        <div className="mb-1">upvotes: {hexagen.upvotes}</div>
        <div>creativity score: {hexagen.points}</div>
      </div>
    </div>
  );
};
